import React, { useEffect, useMemo, useRef, useState } from "react";
import { orderBy } from "@progress/kendo-data-query";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { Splitter, SplitterOnChangeEvent, SplitterPane, SplitterPaneProps } from "@progress/kendo-react-layout";
import { useLocalization } from "@progress/kendo-react-intl";
import moment from "moment";
import { enMessages } from "../../../../../messages/en-US";
import {
  getLocalizedStreamOptions,
  isMlAnomaly,
  isOngoingEndDate,
  normalizeCriticality,
} from "../Overview/Dialogs/AnomalyDetector/AnomalyDetectorForm.utils";
import InsightsAnomaliesSidebar from "./InsightsAnomaliesSidebar";
import { NO_CRITICALITY_FILTER_VALUE } from "./insightsUtils";
import InsightsAnomalyDetailPanel from "./InsightsAnomalyDetailPanel";
import type {
  InsightsAsset,
  InsightSortField,
  InsightAnchorItem,
  InsightAnomalyItem,
  InsightTableInput,
  InsightTableRow,
  ProcessedInsightRow,
} from "./types";

export type { InsightTableRow };
export { insightRowToBackendPayload } from "./insightsUtils";

interface Props {
  data?: InsightTableInput[];
  asset?: InsightsAsset;
  hidePointName?: boolean;
  /** Site-level mode: assetId → name map. Rows show the asset name and the detail panel fetches asset/item data by id. */
  assetNamesById?: Record<string, string>;
  /** Site-level mode: pointId → name map, resolves endpoint names when no asset endpoints are available. */
  pointNamesById?: Record<string, string>;
  /** Site-level mode: deviceId → serial map, resolves gateway labels when no asset gateways are available. */
  deviceNamesById?: Record<string, string>;
}

const CHART_CONTEXT_DAYS = 3;
const CRITICALITY_SORT_ORDER: Record<string, number> = {
  Critical: 4,
  High: 3,
  Medium: 2,
  Low: 1,
};
const initialSort = [{ field: "period", dir: "desc" }] as const;
const TYPE_FILTER_VALUES = ["anomaly", "anchor"] as const;
const CRITICALITY_FILTER_VALUES = ["Critical", "High", "Medium", "Low"] as const;
const SOURCE_FILTER_VALUES = ["ml", "user"] as const;

const toSafeFileNamePart = (value: string): string =>
  value
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, " ")
    .replace(/\.+$/g, "");

const normalizeSearchText = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

function isBackendAnomaly(item: InsightTableInput): item is InsightAnomalyItem {
  return "kind" in item && item.kind === "anomaly";
}

function isAnchorItem(item: InsightTableInput): item is InsightAnchorItem {
  return "kind" in item && item.kind === "anchor";
}

function formatPeriod(startDate: string, endDate: string | null): string {
  const start = moment(startDate);
  if (!start.isValid()) return "";

  if (isOngoingEndDate(endDate)) {
    return `${start.format("DD MMM YYYY HH:mm")} - ${enMessages.custom.ongoing}`;
  }

  const end = moment(endDate);
  if (!end.isValid()) return "";

  return `${start.format("DD MMM YYYY HH:mm")} - ${end.format("DD MMM YYYY HH:mm")}`;
}

function getLatestNote(item: InsightAnomalyItem["data"] | InsightAnchorItem["data"]): string {
  const legacyForm = item.form as typeof item.form & { notes?: typeof item.notes };
  const notes = item.notes ?? legacyForm.notes ?? [];
  if (!notes.length) return "";
  return notes[notes.length - 1]?.comment ?? "";
}

const joinValues = (values: string[] | undefined): string => (Array.isArray(values) ? values.join(", ") : "");

function parsePeriodToEventFilter(period: string): { startDate: Date; endDate: Date } | null {
  const match = period.match(/^(.+?)\s*-\s*(.+)$/);
  if (!match) return null;
  const startStr = match[1].trim();
  const endStr = match[2].trim();
  const startM = moment(startStr, ["DD MMM YYYY HH:mm", "D MMM YYYY HH:mm"]);
  const endM = moment(endStr, ["DD MMM YYYY HH:mm", "D MMM YYYY HH:mm"]);
  if (!startM.isValid() || !endM.isValid()) return null;
  return {
    startDate: startM.toDate(),
    endDate: endM.toDate(),
  };
}

function getAnomalyPeriod(anomaly: InsightTableInput): { startDate: Date; endDate: Date } {
  if (isBackendAnomaly(anomaly)) {
    return {
      startDate: moment(anomaly.data.startDate).toDate(),
      endDate: moment(anomaly.data.endDate).toDate(),
    };
  }

  if (isAnchorItem(anomaly)) {
    return {
      startDate: moment(anomaly.data.startDate).toDate(),
      endDate: moment(anomaly.data.endDate).toDate(),
    };
  }

  const parsed = parsePeriodToEventFilter(anomaly.period);
  if (parsed) {
    return parsed;
  }

  return {
    startDate: moment().subtract(CHART_CONTEXT_DAYS, "days").startOf("day").toDate(),
    endDate: moment().endOf("day").toDate(),
  };
}

function formatStreamsDisplay(
  streams: string[] | undefined,
  localizationService: ReturnType<typeof useLocalization>
): string {
  const streamLabelByValue = new Map(
    getLocalizedStreamOptions(localizationService).map((option) => [option.value, option.text])
  );

  return Array.isArray(streams)
    ? streams.map((stream) => streamLabelByValue.get(stream) ?? stream).join(", ")
    : "";
}

function toInsightTableRow(item: InsightTableInput): InsightTableRow {
  if (!isBackendAnomaly(item) && !isAnchorItem(item)) {
    return item;
  }

  const source = item.data;
  const formCriticality = source.form.criticality;

  return {
    id: source.id || source.anomalyId || `${source.pointId || source.deviceId}-${source.startDate}`,
    period: formatPeriod(source.startDate, source.endDate),
    pointId: source.pointId,
    deviceId: source.deviceId,
    machineId: source.machineId,
    author: source.user,
    anomalyId: source.anomalyId,
    streams: source.streams ?? [],
    criticality: normalizeCriticality(formCriticality) ?? "",
    indicator: joinValues(source.form.indicators),
    rootCause: joinValues(source.form.rootCauses),
    action: joinValues(source.form.actions),
    confidence: source.form.confidence ?? 0,
    estimatedSavings: source.form.estimatedSavings ?? 0,
    notes: getLatestNote(source),
  };
}

function getTypeFilterValue(item: ProcessedInsightRow): string {
  return item.itemKind === "anchor" ? "anchor" : "anomaly";
}

function getLocalizedCriticalityLabel(
  criticality: string,
  localizationService: ReturnType<typeof useLocalization>
): string {
  switch (criticality) {
    case "Critical":
      return localizationService.toLanguageString("custom.critical", "Critical");
    case "High":
      return localizationService.toLanguageString("custom.high", "High");
    case "Medium":
      return localizationService.toLanguageString("custom.medium", "Medium");
    case "Low":
      return localizationService.toLanguageString("custom.low", "Low");
    default:
      return criticality;
  }
}

export default function InsightsAnomaliesView({
  data = [],
  asset,
  hidePointName = false,
  assetNamesById,
  pointNamesById,
  deviceNamesById,
}: Props): React.ReactElement {
  const isSiteLevel = !!assetNamesById;
  const localizationService = useLocalization();
  const [selectedAnomalyId, setSelectedAnomalyId] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [sortField, setSortField] = useState<InsightSortField>("period");
  const [selectedStreamValues, setSelectedStreamValues] = useState<string[]>(() =>
    getLocalizedStreamOptions(localizationService).map((option) => option.value)
  );
  const [selectedCriticalityValues, setSelectedCriticalityValues] = useState<string[]>(() => [...CRITICALITY_FILTER_VALUES]);
  const [selectedTypeValues, setSelectedTypeValues] = useState<string[]>(() => [...TYPE_FILTER_VALUES]);
  const [selectedSourceValues, setSelectedSourceValues] = useState<string[]>(() => [...SOURCE_FILTER_VALUES]);
  const [panes, setPanes] = useState<SplitterPaneProps[]>([
    { size: "30%", min: "320px", collapsible: false },
    { min: "420px", collapsible: false },
  ]);
  const exportRef = useRef<ExcelExport | null>(null);

  const exportFileName = useMemo(() => {
    const assetName = asset?.name?.trim();
    if (!assetName) {
      return `${localizationService.toLanguageString("custom.insights", enMessages.custom.insights)}.xlsx`;
    }

    return `${toSafeFileNamePart(assetName)} ${localizationService.toLanguageString("custom.insights", enMessages.custom.insights)}.xlsx`;
  }, [asset?.name, localizationService]);

  const processedData = useMemo(() => {
    const endpoints = asset?.assetConfigurationDetails?.endpoints ?? [];
    const gateways = asset?.assetConfigurationDetails?.gateways ?? [];
    const withDisplayFields: ProcessedInsightRow[] = data.map((item) => {
      const normalizedItem = toInsightTableRow(item);
      const anomalyPeriod = getAnomalyPeriod(item);
      // An empty pointId with a deviceId means the record is about a device, not a point.
      const resolveName = () => {
        if (normalizedItem.endpointName) return normalizedItem.endpointName;

        if (normalizedItem.pointId) {
          return (
            endpoints.find((endpoint) => endpoint.id === normalizedItem.pointId)?.name ??
            pointNamesById?.[normalizedItem.pointId]
          );
        }

        if (normalizedItem.deviceId) {
          const gateway = gateways.find((item) => item.id === normalizedItem.deviceId);
          // Serial, not name: it is the only label available at site level, and it identifies the unit.
          return gateway?.serialNumber || deviceNamesById?.[normalizedItem.deviceId] || gateway?.name;
        }

        return undefined;
      };
      const endpointName = resolveName();

      return {
        ...normalizedItem,
        itemKind: isAnchorItem(item) ? "anchor" : isBackendAnomaly(item) ? "anomaly" : "table",
        targetKind: normalizedItem.deviceId ? "device" : "point",
        rawAnomaly: item,
        endpointDisplay: endpointName ?? "-",
        assetDisplay: normalizedItem.machineId ? assetNamesById?.[normalizedItem.machineId] : undefined,
        streamsDisplay: formatStreamsDisplay(normalizedItem.streams, localizationService),
        startDate: anomalyPeriod.startDate,
        endDate: anomalyPeriod.endDate,
        source: isMlAnomaly(normalizedItem.anomalyId) ? "ml" : "user",
      };
    });

    const sortedItems = [...orderBy(withDisplayFields, initialSort as any)];

    sortedItems.sort((left, right) => {
      if (sortField === "criticality") {
        const criticalityDiff =
          (CRITICALITY_SORT_ORDER[right.criticality] ?? 0) - (CRITICALITY_SORT_ORDER[left.criticality] ?? 0);

        if (criticalityDiff !== 0) {
          return criticalityDiff;
        }
      }

      const endDiff = right.endDate.getTime() - left.endDate.getTime();
      if (endDiff !== 0) {
        return endDiff;
      }

      return right.startDate.getTime() - left.startDate.getTime();
    });

    return sortedItems;
  }, [asset?.assetConfigurationDetails?.endpoints, asset?.assetConfigurationDetails?.gateways, assetNamesById, pointNamesById, deviceNamesById, data, localizationService, sortField]);

  const filteredData = useMemo(() => {
    const normalizedSearchValue = normalizeSearchText(searchValue);
    const selectedStreams = new Set(selectedStreamValues);
    const selectedCriticalities = new Set(selectedCriticalityValues);
    const selectedTypes = new Set(selectedTypeValues);
    const selectedSources = new Set(selectedSourceValues);

    const filteredByPopupSelections = processedData.filter(
      (item) =>
        selectedStreams.size > 0 &&
        selectedSources.has(item.source) &&
        selectedTypes.has(getTypeFilterValue(item)) &&
        // Criticality doesn't apply to anchors (the tile hides it for them); the Type section controls those.
        (item.itemKind === "anchor" ||
          selectedCriticalities.has(item.criticality || NO_CRITICALITY_FILTER_VALUE)) &&
        Array.isArray(item.streams) &&
        item.streams.length > 0 &&
        item.streams.every((stream) => selectedStreams.has(stream))
    );

    if (!normalizedSearchValue) {
      return filteredByPopupSelections;
    }

    const searchTerms = normalizedSearchValue.split(" ").filter(Boolean);

    return filteredByPopupSelections.filter((item) => {
      const localizedCriticality = getLocalizedCriticalityLabel(item.criticality, localizationService);
      const searchableText = normalizeSearchText(
        [
          item.period,
          item.assetDisplay,
          item.endpointDisplay,
          item.streamsDisplay,
          item.criticality,
          localizedCriticality,
          item.author,
          item.anomalyId,
          getTypeFilterValue(item),
        ].filter(Boolean).join(" ")
      );

      return searchTerms.every((term) => searchableText.includes(term));
    });
  }, [processedData, searchValue, selectedCriticalityValues, selectedSourceValues, selectedStreamValues, selectedTypeValues, localizationService]);

  useEffect(() => {
    if (filteredData.length === 0) {
      setSelectedAnomalyId(null);
      return;
    }

    if (!selectedAnomalyId || !filteredData.some((item) => item.id === selectedAnomalyId)) {
      setSelectedAnomalyId(filteredData[0].id);
    }
  }, [filteredData, selectedAnomalyId]);

  const handleExcelExport = () => {
    if (exportRef.current) {
      exportRef.current.save();
    }
  };

  const handleSplitterChange = (event: SplitterOnChangeEvent) => {
    setPanes(event.newState);
  };

  const selectedItem = useMemo(
    () => filteredData.find((item) => item.id === selectedAnomalyId) ?? null,
    [filteredData, selectedAnomalyId]
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <ExcelExport data={processedData} ref={exportRef} fileName={exportFileName}>
        <ExcelExportColumn field="period" title={localizationService.toLanguageString("custom.period", enMessages.custom.period)} width={200} />
        {isSiteLevel && (
          <ExcelExportColumn field="assetDisplay" title={localizationService.toLanguageString("custom.asset", enMessages.custom.asset)} width={150} />
        )}
        <ExcelExportColumn field="endpointDisplay" title={localizationService.toLanguageString("custom.endpoint", enMessages.custom.endpoint)} width={150} />
        <ExcelExportColumn field="author" title={localizationService.toLanguageString("custom.author", enMessages.custom.author)} width={120} />
        <ExcelExportColumn field="streamsDisplay" title={localizationService.toLanguageString("custom.streams", enMessages.custom.streams)} width={150} />
        <ExcelExportColumn
          field="criticality"
          title={localizationService.toLanguageString("custom.criticality", enMessages.custom.criticality)}
          width={100}
        />
        <ExcelExportColumn field="indicator" title={localizationService.toLanguageString("custom.indicator", enMessages.custom.indicator)} width={120} />
        <ExcelExportColumn field="rootCause" title={localizationService.toLanguageString("custom.rootCause", enMessages.custom.rootCause)} width={150} />
        <ExcelExportColumn field="action" title={localizationService.toLanguageString("custom.action", enMessages.custom.action)} width={150} />
        <ExcelExportColumn field="confidence" title={localizationService.toLanguageString("custom.confidence", enMessages.custom.confidence)} width={100} />
        <ExcelExportColumn field="estimatedSavings" title={localizationService.toLanguageString("custom.estimatedSavings", enMessages.custom.estimatedSavings)} width={150} />
        <ExcelExportColumn field="notes" title={localizationService.toLanguageString("custom.notes", enMessages.custom.notes)} width={250} />
      </ExcelExport>
      <Splitter
        panes={panes}
        onChange={handleSplitterChange}
        style={{ height: "100%", minHeight: 0, width: "100%", border: "none" }}
      >
        <SplitterPane orientation="horizontal" overlay={false} containsSplitter={false}>
          <InsightsAnomaliesSidebar
            items={filteredData}
            allItemsCount={processedData.length}
            searchValue={searchValue}
            sortField={sortField}
            selectedStreamValues={selectedStreamValues}
            selectedCriticalityValues={selectedCriticalityValues}
            selectedTypeValues={selectedTypeValues}
            selectedSourceValues={selectedSourceValues}
            selectedAnomalyId={selectedAnomalyId}
            hidePointName={hidePointName}
            onSearchChange={setSearchValue}
            onSortFieldChange={setSortField}
            onSelectedStreamsChange={setSelectedStreamValues}
            onSelectedCriticalitiesChange={setSelectedCriticalityValues}
            onSelectedTypesChange={setSelectedTypeValues}
            onSelectedSourcesChange={setSelectedSourceValues}
            onSelectAnomaly={setSelectedAnomalyId}
            onExport={handleExcelExport}
          />
        </SplitterPane>
        <SplitterPane orientation="horizontal" overlay={false} containsSplitter={false}>
          <div className="flex h-full min-h-0 min-w-0 overflow-hidden">
            <InsightsAnomalyDetailPanel item={selectedItem} asset={asset} fetchDetailData={isSiteLevel} />
          </div>
        </SplitterPane>
      </Splitter>
    </div>
  );
}
